import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BASE_MQ = {
  "Abitazione Residenziale": 2200,
  "Locale Commerciale / Negozio": 1700,
  "Ufficio / Studio": 1800,
};
const PIANO_F = {
  "Piano Terra": 0.97,
  "Piano Intermedio": 1.0,
  "Ultimo Piano / Attico": 1.06,
};
const STATO_F = {
  "Nuovo / Ristrutturato": 1.36,
  Abitabile: 1.0,
  "Da ristrutturare": 0.68,
};
const RISC_F = {
  Autonomo: 1.02,
  "Centralizzato con valvole": 1.0,
  "Pompa di calore / Ibrido": 1.04,
};

const eur = (n) => `€ ${new Intl.NumberFormat("it-IT", { maximumFractionDigits: 0 }).format(n)}`;

export default function CalculatorForm() {
  const [form, setForm] = useState({
    destinazione: "Abitazione Residenziale",
    mq: "",
    zona: "",
    piano: "Piano Intermedio",
    stato: "Abitabile",
    riscaldamento: "Autonomo",
    spese: "",
    descrizione: "",
    email: "",
  });
  const [result, setResult] = useState(null);
  const [saving, setSaving] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const inputCls =
    "w-full bg-white border border-[var(--ink)]/20 rounded-md p-3 text-sm focus:border-[var(--gold)] outline-none transition-colors";
  const labelCls = "block font-mono-lbl text-xs uppercase tracking-wider text-[var(--ink)]/80 mb-2";

  const calcola = async (e) => {
    e.preventDefault();
    const mq = parseFloat(form.mq);
    if (!mq || mq <= 0) {
      toast.error("Inserisci una metratura valida in mq.");
      return;
    }
    if (!form.zona.trim()) {
      toast.error("Indica la zona dell'immobile.");
      return;
    }
    if (!/\S+@\S+\.\S+/.test(form.email)) {
      toast.error("Inserisci la tua email per vedere la stima: ti invieremo il dettaglio della valutazione.");
      return;
    }
    const spese = parseFloat(form.spese) || 0;
    // Valori al mq tarati dall'agenzia (Modena): nuovo ~3.000, abitabile ~2.200,
    // da ristrutturare ~1.500 (2.000 in centro)
    let statoF = STATO_F[form.stato];
    if (
      form.destinazione === "Abitazione Residenziale" &&
      form.stato === "Da ristrutturare" &&
      form.zona.toLowerCase().includes("centro")
    ) {
      statoF = 0.91;
    }
    let fattore = BASE_MQ[form.destinazione] * PIANO_F[form.piano] * statoF * RISC_F[form.riscaldamento];
    if (spese > 200) fattore *= 0.96;
    else if (spese > 100) fattore *= 0.98;

    const valore = mq * fattore;
    const stima = {
      min: Math.round(valore * 0.94),
      max: Math.round(valore * 1.06),
      alMq: Math.round(fattore),
    };
    setResult(stima);

    setSaving(true);
    try {
      await axios.post(`${API}/estimates`, {
        destinazione: form.destinazione,
        mq,
        zona: form.zona,
        piano: form.piano,
        stato: form.stato,
        riscaldamento: form.riscaldamento,
        spese_condominiali: spese,
        descrizione: form.descrizione,
        email: form.email,
        stima_min: stima.min,
        stima_max: stima.max,
      });
    } catch (err) {
      // la stima resta visibile anche se il salvataggio fallisce
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[var(--paper)] border border-[var(--ink)]/15 rounded-xl p-8 md:p-10 shadow-sm">
      <form className="space-y-6" onSubmit={calcola} data-testid="calculator-form">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="calc-dest" className={labelCls}>Destinazione d'Uso</label>
            <select id="calc-dest" data-testid="calc-destinazione" value={form.destinazione} onChange={update("destinazione")} className={inputCls}>
              {Object.keys(BASE_MQ).map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="calc-mq" className={labelCls}>Metratura (Mq)</label>
            <input id="calc-mq" data-testid="calc-mq" type="number" min="1" placeholder="Es. 100" value={form.mq} onChange={update("mq")} className={`${inputCls} font-mono-lbl`} />
          </div>
          <div>
            <label htmlFor="calc-zona" className={labelCls}>Zona Immobile</label>
            <input id="calc-zona" data-testid="calc-zona" type="text" placeholder="Quartiere o indirizzo indicativo" value={form.zona} onChange={update("zona")} className={inputCls} />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 pt-4 border-t border-[var(--ink)]/10">
          <div>
            <label htmlFor="calc-piano" className={labelCls}>Piano</label>
            <select id="calc-piano" data-testid="calc-piano" value={form.piano} onChange={update("piano")} className={inputCls}>
              {Object.keys(PIANO_F).map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="calc-stato" className={labelCls}>Stato Immobile</label>
            <select id="calc-stato" data-testid="calc-stato" value={form.stato} onChange={update("stato")} className={inputCls}>
              {Object.keys(STATO_F).map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="calc-risc" className={labelCls}>Riscaldamento</label>
            <select id="calc-risc" data-testid="calc-riscaldamento" value={form.riscaldamento} onChange={update("riscaldamento")} className={inputCls}>
              {Object.keys(RISC_F).map((k) => <option key={k}>{k}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="calc-spese" className={labelCls}>Spese Condominiali (€/mese)</label>
            <input id="calc-spese" data-testid="calc-spese" type="number" min="0" placeholder="Es. 60" value={form.spese} onChange={update("spese")} className={`${inputCls} font-mono-lbl`} />
          </div>
        </div>

        <div className="pt-4 border-t border-[var(--ink)]/10">
          <label htmlFor="calc-desc" className={labelCls}>Descrizione casa e accessori (balcone, garage, ascensore)</label>
          <textarea id="calc-desc" data-testid="calc-descrizione" rows="2" placeholder="Dettagli sulle pertinenze..." value={form.descrizione} onChange={update("descrizione")} className={inputCls}></textarea>
        </div>

        <div className="flex flex-col sm:flex-row items-end justify-between gap-4 pt-4 border-t border-[var(--ink)]/10">
          <div className="w-full sm:flex-1">
            <label htmlFor="calc-email" className={labelCls}>La tua email — serve per vedere la stima</label>
            <input id="calc-email" data-testid="calc-email" type="email" placeholder="nome@email.it" value={form.email} onChange={update("email")} className={inputCls} />
            <span className="block text-[11px] text-[var(--ink)]/60 mt-1 font-mono-lbl">
              Ti inviamo il dettaglio e resti aggiornato sul valore della tua zona.
            </span>
          </div>
          <button
            type="submit"
            data-testid="calc-submit"
            className="w-full sm:w-auto px-8 py-3.5 bg-[var(--ink)] text-[var(--gold-light)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:bg-[var(--gold)] hover:text-[var(--ink)] transition-colors"
          >
            Calcola Valore Ora
          </button>
        </div>
      </form>

      {result && (
        <div
          data-testid="calc-result"
          className="mt-8 bg-[var(--ink)] text-white rounded-lg p-8 grid grid-cols-1 md:grid-cols-3 gap-6 items-center"
        >
          <div>
            <span className="font-mono-lbl text-[10px] uppercase tracking-widest text-[var(--gold-light)] block mb-1">
              Valore stimato
            </span>
            <span className="font-display font-black text-2xl md:text-3xl" data-testid="calc-result-range">
              {eur(result.min)} – {eur(result.max)}
            </span>
          </div>
          <div className="font-mono-lbl text-xs space-y-1 text-white/75">
            <p>Valore medio al mq: <span className="text-[var(--gold-light)]">{eur(result.alMq)}</span></p>
            <p>Tipologia: {form.destinazione}</p>
            <p>Zona: {form.zona}</p>
          </div>
          <div className="space-y-3">
            <p className="text-xs text-white/70 leading-relaxed">
              Stima parametrica indicativa: la valutazione reale richiede il sopralluogo tecnico gratuito.
            </p>
            <Link
              to="/#contatto"
              data-testid="calc-result-cta"
              className="inline-block px-5 py-2.5 bg-[var(--gold)] text-[var(--ink)] font-mono-lbl text-xs uppercase tracking-widest font-bold rounded hover:bg-white transition-colors"
            >
              Prenota Sopralluogo →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
